<template>
  <Teleport to="body">
    <transition name="image-viewer-fade">
      <div v-if="visible" class="image-viewer-overlay" @click="handleOverlayClick">
      <div class="image-viewer-container" @click.stop>
      <!-- 关闭按钮 -->
      <button class="close-btn" @click="close">
        <i class="fa-solid fa-times"></i>
      </button>
      
      <!-- 工具栏 -->
      <div class="toolbar">
        <button class="tool-btn" @click="zoomIn" title="放大">
          <i class="fa-solid fa-search-plus"></i>
        </button>
        <button class="tool-btn" @click="zoomOut" title="缩小">
          <i class="fa-solid fa-search-minus"></i>
        </button>
        <button class="tool-btn" @click="resetZoom" title="重置">
          <i class="fa-solid fa-expand-arrows-alt"></i>
        </button>
        <button class="tool-btn" @click="rotateLeft" title="左旋转">
          <i class="fa-solid fa-undo"></i>
        </button>
        <button class="tool-btn" @click="rotateRight" title="右旋转">
          <i class="fa-solid fa-redo"></i>
        </button>
        <button class="tool-btn" @click="downloadImage" title="下载">
          <i class="fa-solid fa-download"></i>
        </button>
      </div>
      
      <!-- 图片容器 -->
      <div class="image-container" ref="imageContainer">
        <img 
          ref="image"
          :src="imageSrc" 
          :alt="imageAlt"
          class="viewer-image"
          :style="imageStyle"
          @load="onImageLoad"
          @error="onImageError"
          @mousedown="startDrag"
          @wheel="handleWheel"
          @mouseenter="showTooltip = true"
          @mouseleave="showTooltip = false"
          @dblclick="resetZoom"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove"
          @touchend="handleTouchEnd"
        />
        
        <!-- 双击提示 -->
        <div v-if="showTooltip" class="tooltip">
          双击重置缩放
        </div>
      </div>
      
      <!-- 图片信息 -->
      <div class="image-info" v-if="showInfo">
        <div class="info-item">
          <span class="info-label">文件名:</span>
          <span class="info-value">{{ imageName }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">尺寸:</span>
          <span class="info-value">{{ imageSize }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">缩放:</span>
          <span class="info-value">{{ Math.round(scale * 100) }}%</span>
        </div>
        </div>
      </div>
    </div>
  </transition>
  </Teleport>
</template>

<script>
export default {
  name: 'ImageViewer',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    imageSrc: {
      type: String,
      default: ''
    },
    imageAlt: {
      type: String,
      default: '图片'
    },
    showInfo: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      scale: 1,
      rotation: 0,
      translateX: 0,
      translateY: 0,
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      imageWidth: 0,
      imageHeight: 0,
      showTooltip: false,
      // 触摸手势相关
      touchStartX: 0,
      touchStartY: 0,
      lastTouchX: 0,
      lastTouchY: 0,
      startDistance: 0,
      startScale: 1,
      isPinching: false
    }
  },
  computed: {
    imageStyle() {
      return {
        transform: `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale}) rotate(${this.rotation}deg)`,
        cursor: this.isDragging ? 'grabbing' : 'grab'
      }
    },
    imageName() {
      if (!this.imageSrc) return ''
      const parts = this.imageSrc.split('/')
      return parts[parts.length - 1]
    },
    imageSize() {
      if (!this.imageWidth || !this.imageHeight) return ''
      return `${this.imageWidth} × ${this.imageHeight}`
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetView()
        this.addEventListeners()
      } else {
        this.removeEventListeners()
      }
    }
  },
  methods: {
    close() {
      this.$emit('close')
    },
    
    handleOverlayClick(event) {
      if (event.target === event.currentTarget) {
        this.close()
      }
    },
    
    zoomIn() {
      this.scale = Math.min(this.scale * 1.2, 5)
    },
    
    zoomOut() {
      this.scale = Math.max(this.scale / 1.2, 0.1)
    },
    
    resetZoom() {
      this.scale = 1
      this.translateX = 0
      this.translateY = 0
      this.rotation = 0
    },
    
    rotateLeft() {
      this.rotation -= 90
    },
    
    rotateRight() {
      this.rotation += 90
    },
    
    resetView() {
      this.scale = 1
      this.rotation = 0
      this.translateX = 0
      this.translateY = 0
      this.isDragging = false
    },
    
    onImageLoad() {
      const img = this.$refs.image
      this.imageWidth = img.naturalWidth
      this.imageHeight = img.naturalHeight
    },
    
    onImageError() {
      console.error('图片加载失败:', this.imageSrc)
    },
    
    startDrag(event) {
      event.preventDefault()
      this.isDragging = true
      this.dragStartX = event.clientX - this.translateX
      this.dragStartY = event.clientY - this.translateY
      
      document.addEventListener('mousemove', this.onDrag)
      document.addEventListener('mouseup', this.stopDrag)
    },
    
    onDrag(event) {
      if (!this.isDragging) return
      
      this.translateX = event.clientX - this.dragStartX
      this.translateY = event.clientY - this.dragStartY
    },
    
    stopDrag() {
      this.isDragging = false
      document.removeEventListener('mousemove', this.onDrag)
      document.removeEventListener('mouseup', this.stopDrag)
    },
    
    handleWheel(event) {
      event.preventDefault()
      
      const delta = event.deltaY > 0 ? 0.9 : 1.1
      this.scale = Math.max(0.1, Math.min(5, this.scale * delta))
    },
    
    downloadImage() {
      const link = document.createElement('a')
      link.href = this.imageSrc
      link.download = this.imageName || 'image'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    
    handleKeydown(event) {
      switch (event.key) {
        case 'Escape':
          this.close()
          break
        case '+':
        case '=':
          this.zoomIn()
          break
        case '-':
          this.zoomOut()
          break
        case '0':
          this.resetZoom()
          break
        case 'ArrowLeft':
          this.rotateLeft()
          break
        case 'ArrowRight':
          this.rotateRight()
          break
      }
    },
    
    addEventListeners() {
      document.addEventListener('keydown', this.handleKeydown)
    },
    
    removeEventListeners() {
      document.removeEventListener('keydown', this.handleKeydown)
      document.removeEventListener('mousemove', this.onDrag)
      document.removeEventListener('mouseup', this.stopDrag)
    },

    // 触摸开始
    handleTouchStart(e) {
      if (e.touches.length === 1) {
        // 单指拖拽
        this.isDragging = true
        this.touchStartX = e.touches[0].clientX - this.translateX
        this.touchStartY = e.touches[0].clientY - this.translateY
      } else if (e.touches.length === 2) {
        // 双指缩放
        this.isPinching = true
        this.isDragging = false
        this.startDistance = this.getDistance(e.touches)
        this.startScale = this.scale
      }
    },

    // 触摸移动
    handleTouchMove(e) {
      e.preventDefault() // 防止页面滚动
      
      if (this.isPinching && e.touches.length === 2) {
        // 双指缩放逻辑
        const currentDistance = this.getDistance(e.touches)
        const scaleRatio = currentDistance / this.startDistance
        let newScale = this.startScale * scaleRatio
        // 限制缩放范围
        newScale = Math.max(0.1, Math.min(5, newScale))
        this.scale = newScale
      } else if (this.isDragging && e.touches.length === 1) {
        // 单指移动逻辑
        this.translateX = e.touches[0].clientX - this.touchStartX
        this.translateY = e.touches[0].clientY - this.touchStartY
      }
    },

    // 触摸结束
    handleTouchEnd(e) {
      if (e.touches.length === 0) {
        this.isDragging = false
        this.isPinching = false
      } else if (e.touches.length === 1 && this.isPinching) {
        // 如果松开一指，切换回拖拽模式，重置起始点防止跳变
        this.isPinching = false
        this.isDragging = true
        this.touchStartX = e.touches[0].clientX - this.translateX
        this.touchStartY = e.touches[0].clientY - this.translateY
      }
    },

    // 计算双指距离
    getDistance(touches) {
      const dx = touches[0].clientX - touches[1].clientX
      const dy = touches[0].clientY - touches[1].clientY
      return Math.sqrt(dx * dx + dy * dy)
    }
  },
  
  beforeUnmount() {
    this.removeEventListeners()
  }
}
</script>

<style scoped>
.image-viewer-overlay {
  position: fixed;
  inset: 0;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.9);
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 过渡动画 */
.image-viewer-fade-enter-active,
.image-viewer-fade-leave-active {
  transition: opacity 0.3s ease;
}

.image-viewer-fade-enter-from,
.image-viewer-fade-leave-to {
  opacity: 0;
}

.image-viewer-fade-enter-to,
.image-viewer-fade-leave-from {
  opacity: 1;
}

.image-viewer-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  color: white;
  font-size: 20px;
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.toolbar {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
  background: rgba(0, 0, 0, 0.5);
  padding: 10px;
  border-radius: 25px;
  z-index: 10;
}

.tool-btn {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  color: white;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tool-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.image-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
}

.viewer-image {
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
  transition: transform 0.3s ease;
  user-select: none;
  -webkit-user-drag: none;
}

.tooltip {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  pointer-events: none;
  z-index: 20;
  animation: tooltipFadeIn 0.3s ease;
}

@keyframes tooltipFadeIn {
  from {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.8);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
}

.image-info {
  position: absolute;
  top: 80px;
  left: 20px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 15px;
  border-radius: 10px;
  font-family: '爱点风雅黑', sans-serif;
  z-index: 10;
}

.info-item {
  display: flex;
  margin-bottom: 5px;
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-label {
  font-weight: bold;
  margin-right: 10px;
  min-width: 50px;
}

.info-value {
  color: #ccc;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .toolbar {
    bottom: 10px;
    left: 10px;
    right: 10px;
    transform: none;
    justify-content: center;
    flex-wrap: wrap;
  }
  
  .close-btn {
    top: 10px;
    right: 10px;
    width: 40px;
    height: 40px;
    font-size: 16px;
  }
  
  .tool-btn {
    width: 35px;
    height: 35px;
    font-size: 14px;
  }
  
  .image-info {
    top: 60px;
    left: 10px;
    right: 10px;
    padding: 10px;
    font-size: 14px;
  }
  
  .viewer-image {
    max-width: 95%;
    max-height: 95%;
  }
}
</style>